/**
 * Per party (employee) × group discount % → commission on Employee sales report.
 * Rates can be FY-specific (e.g. 2025-26) or legacy (all years).
 * commission amount = group sale × discount % / 100
 */
import { collection, doc, getDocs, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { normalizeEmployeeName } from "./monthlyIncentivePartySalesService";

export const EMP_PARTY_GROUP_COMMISSION_COL = "employee_party_group_commissions";
const DOC_SEP = "__";

export function partyCommissionKey(partyName) {
  return normalizeEmployeeName(partyName || "")
    .replace(/[^\w.-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

/**
 * @param {string} partyName
 * @param {string} groupId
 * @param {number|null|undefined} fyStartYear FY April year (e.g. 2025 for 2025-26). null = all years / legacy
 */
export function partyGroupCommissionDocId(partyName, groupId, fyStartYear = null) {
  const pk = partyCommissionKey(partyName);
  const gid = String(groupId || "").trim();
  if (!pk || !gid) return "";
  const fy = Number(fyStartYear);
  if (Number.isFinite(fy) && fy > 1990) {
    return `${pk}${DOC_SEP}fy${fy}${DOC_SEP}${gid}`;
  }
  return `${pk}${DOC_SEP}${gid}`;
}

function docMatchesParty(docSnap, targetNorm, partyKey) {
  if (!targetNorm) return false;
  const data = docSnap.data() || {};
  const candidates = [
    data.partyNameLower,
    data.partyName,
    data.party,
    data.employeeName,
  ].map((s) => normalizeEmployeeName(s));
  if (candidates.some((c) => c && c === targetNorm)) return true;
  if (partyKey && (docSnap.id === partyKey || docSnap.id.startsWith(`${partyKey}${DOC_SEP}`))) {
    return true;
  }
  // loose: either side contains the other (handles extra staff codes)
  for (const c of candidates) {
    if (!c) continue;
    if (c.includes(targetNorm) || targetNorm.includes(c)) return true;
  }
  return false;
}

/**
 * Prefer FY-specific row, else legacy (no fy / all).
 * @returns {"fy"|"legacy"|null}
 */
function rowFyKind(data, fyStartYear) {
  const fy = Number(fyStartYear);
  const rowFy = data.fyStartYear;
  if (rowFy == null || rowFy === "" || rowFy === "all") return "legacy";
  const n = Number(rowFy);
  if (!Number.isFinite(n)) return "legacy";
  if (Number.isFinite(fy) && n === fy) return "fy";
  return null; // other FY — skip when targeting a year
}

/**
 * Map group name (lower) + groupId → discount % for a party.
 * @param {string} partyName
 * @param {number|null|undefined} fyStartYear e.g. 2025 for FY 2025-26
 */
export async function loadPartyGroupCommissionRateMap(partyName, fyStartYear = null) {
  const target = normalizeEmployeeName(partyName);
  /** @type {Map<string, number>} */
  const byGroupName = new Map();
  /** @type {Map<string, number>} */
  const byGroupId = new Map();
  if (!target) {
    return { byGroupName, byGroupId, partyName: "", rows: [], fyStartYear: fyStartYear ?? null };
  }

  const pk = partyCommissionKey(partyName);
  // Full scan — collection is small; avoids empty-index / query misses
  const snap = await getDocs(collection(db, EMP_PARTY_GROUP_COMMISSION_COL));

  /** @type {Array<{ id: string, partyName: string, groupId: string, groupName: string, discountPercent: number, kind: string, fyStartYear: number|null }>} */
  const rows = [];
  /** Prefer FY-specific over legacy when writing to maps */
  const idBest = new Map(); // groupId → { pct, kind rank }
  const nameBest = new Map(); // nameLower → { pct, kind rank }

  const rank = (kind) => (kind === "fy" ? 2 : kind === "legacy" ? 1 : 0);

  for (const d of snap.docs) {
    if (!docMatchesParty(d, target, pk)) continue;
    const data = d.data() || {};
    const kind = rowFyKind(data, fyStartYear);
    // When no FY requested, accept all (prefer any); when FY set, only fy+legacy
    if (fyStartYear != null && Number.isFinite(Number(fyStartYear)) && kind == null) {
      continue;
    }
    const useKind = kind || "legacy";
    const groupId = String(data.groupId || "").trim();
    const groupName = String(data.groupName || data.group || "").trim();
    const pct = Number(data.discountPercent ?? data.commissionPercent ?? data.discount ?? 0);
    if (!Number.isFinite(pct) || pct < 0) continue;

    const rowFy =
      data.fyStartYear != null && data.fyStartYear !== "" && data.fyStartYear !== "all"
        ? Number(data.fyStartYear)
        : null;

    rows.push({
      id: d.id,
      partyName: String(data.partyName || "").trim(),
      groupId,
      groupName,
      discountPercent: pct,
      kind: useKind,
      fyStartYear: Number.isFinite(rowFy) ? rowFy : null,
    });

    if (groupId) {
      const prev = idBest.get(groupId);
      if (!prev || rank(useKind) >= rank(prev.kind)) {
        idBest.set(groupId, { pct, kind: useKind });
      }
    }
    if (groupName) {
      const key = groupName.toLowerCase();
      const prev = nameBest.get(key);
      if (!prev || rank(useKind) >= rank(prev.kind)) {
        nameBest.set(key, { pct, kind: useKind });
      }
    }
  }

  for (const [id, v] of idBest.entries()) byGroupId.set(id, v.pct);
  for (const [name, v] of nameBest.entries()) byGroupName.set(name, v.pct);

  return {
    byGroupName,
    byGroupId,
    partyName: String(partyName || "").trim(),
    rows,
    fyStartYear: fyStartYear ?? null,
  };
}

/** Distinct party names that already have commission settings. */
export async function listPartiesWithCommissionSettings() {
  const snap = await getDocs(collection(db, EMP_PARTY_GROUP_COMMISSION_COL));
  const set = new Set();
  for (const d of snap.docs) {
    const data = d.data() || {};
    const n = String(data.partyName || data.party || data.employeeName || "").trim();
    if (n) set.add(n);
  }
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

function fromRateMap(map, key) {
  if (!(map instanceof Map) || !map.has(key)) return null;
  const v = Number(map.get(key));
  return Number.isFinite(v) ? v : null;
}

/**
 * Resolve commission % for one group label for this party.
 * Uses party×group rates (incl. groupId via masterGroups), then global group fallback.
 */
export function resolvePartyGroupCommissionPercent(
  groupLabel,
  ratePack,
  fallBackGroupMap,
  masterGroups = []
) {
  const name = String(groupLabel || "").trim();
  if (!name) return 0;
  const lower = name.toLowerCase();

  if (ratePack) {
    let pct = fromRateMap(ratePack.byGroupName, lower);
    if (pct != null) return pct;

    for (const g of masterGroups || []) {
      if (!g || typeof g !== "object") continue;
      const gn = String(g.name || "").trim().toLowerCase();
      if (!gn) continue;
      if (gn === lower || lower.includes(gn) || gn.includes(lower)) {
        if (g.id) {
          pct = fromRateMap(ratePack.byGroupId, g.id);
          if (pct != null) return pct;
        }
        pct = fromRateMap(ratePack.byGroupName, gn);
        if (pct != null) return pct;
      }
    }

    if (ratePack.byGroupName instanceof Map) {
      for (const [k, v] of ratePack.byGroupName.entries()) {
        if (!k) continue;
        if (lower.includes(k) || k.includes(lower)) {
          const n = Number(v);
          if (Number.isFinite(n)) return n;
        }
      }
    }

    if (ratePack.byGroupId instanceof Map && ratePack.byGroupId.size === 1) {
      // single-group package — rare
    }
  }

  if (fallBackGroupMap instanceof Map) {
    let pct = fromRateMap(fallBackGroupMap, lower);
    if (pct != null) return pct;
    for (const [k, v] of fallBackGroupMap.entries()) {
      if (lower.includes(k) || k.includes(lower)) {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
    }
  }
  return 0;
}

/**
 * Save group discount % rows for one party (employee), optional FY scope.
 * @param {string} partyName
 * @param {{ groupId: string, groupName: string, percent: number }[]} rows
 * @param {number|null|undefined} fyStartYear e.g. 2025 for 2025-26 (null = all years)
 */
export async function savePartyGroupCommissionPercents(partyName, rows, fyStartYear = null) {
  const name = String(partyName || "").trim();
  if (!name) throw new Error("Select a party / employee name");
  const partyNameLower = normalizeEmployeeName(name);
  if (!partyNameLower) throw new Error("Invalid party name");

  const list = (rows || []).filter((r) => r?.groupId);
  if (!list.length) return 0;

  const fyNum = Number(fyStartYear);
  const hasFy = Number.isFinite(fyNum) && fyNum > 1990;

  const CHUNK = 400;
  let saved = 0;
  for (let i = 0; i < list.length; i += CHUNK) {
    const batch = writeBatch(db);
    const slice = list.slice(i, i + CHUNK);
    for (const row of slice) {
      const n = Number(row.percent);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        throw new Error(`Invalid discount % for “${row.groupName || row.groupId}”`);
      }
      const docId = partyGroupCommissionDocId(name, row.groupId, hasFy ? fyNum : null);
      if (!docId) continue;
      /** @type {Record<string, unknown>} */
      const payload = {
        partyName: name,
        partyNameLower,
        groupId: String(row.groupId).trim(),
        groupName: String(row.groupName || "").trim(),
        groupNameLower: String(row.groupName || "")
          .trim()
          .toLowerCase(),
        discountPercent: n,
        commissionPercent: n,
        updatedAt: new Date(),
      };
      if (hasFy) {
        payload.fyStartYear = fyNum;
      } else {
        payload.fyStartYear = null;
      }
      batch.set(doc(db, EMP_PARTY_GROUP_COMMISSION_COL, docId), payload, { merge: true });
    }
    await batch.commit();
    saved += slice.length;
  }
  return saved;
}
